import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Button from 'src/components/atoms/button/button';
import { Form, FormError, FormItem, FormRow, ReadOnly } from 'src/components/atoms/form';
import Label from 'src/components/atoms/label/label';
import { Select, TOptionType } from 'src/components/atoms/select/select';
import { Switcher } from 'src/components/atoms/switcher/switcher';
import { User } from 'src/models/user.model';
import { RootState, useAppDispatch, useAppSelector } from 'src/reducers';
import { getRoles } from 'src/reducers/roles.reducer';
import { updateUser } from 'src/reducers/update-user.reducer';
import { resetUserState, searchUserById } from 'src/reducers/user.reducer';
import { rolesDefaultParams } from 'src/utils/pagination-utils';
import { defaultUser } from 'src/utils/user-utils';
import { validationSchema } from 'src/utils/validation-utils';
import styles from '../employes.module.scss';
import Flex from '../../../atoms/flex/flex';

export const UserForm = () => {
  const params = useParams();
  const dispatch = useAppDispatch();

  const id = Number(params.id);

  const { user, searchSuccess, loading } = useAppSelector((state: RootState) => state.user);
  const { roles } = useAppSelector((state: RootState) => state.roles);

  const authorityOptions: TOptionType[] = roles.map(role => {
    return { label: role.role.name, value: role.role.id };
  }) as TOptionType[];

  const [editable, setEditable] = useState<boolean>(false);
  const [active, setActive] = useState<boolean>(false);

  const {
    register,
    setValue,
    watch,
    handleSubmit,

    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultUser,
  });

  const onActiveSwitch = () => {
    const newActiveState = !active;
    setActive(newActiveState);
    setValue('active', newActiveState);
  };

  const watchEmail = watch('email');
  const watchRoleId = watch('authority.id');

  const selectAuthority = data => {
    setValue('authority.id', data.value as number);
  };

  const onSave = () => {
    const updatedUser: User = {
      ...user,
      email: watchEmail || user.email,
      authority: {
        id: watchRoleId || user.authority.id,
      },
      active,
    };
    dispatch(updateUser({ user: updatedUser }));
    setEditable(false);
  };

  useEffect(() => {
    if (id) {
      dispatch(searchUserById({ id: id }));
      dispatch(getRoles({ pagination: rolesDefaultParams }));
    }

    return () => {
      dispatch(resetUserState());
    };
  }, [id, dispatch]);

  useEffect(() => {
    if (searchSuccess) {
      setValue('email', user.email);
      setValue('authority.id', user.authority.id);
      setValue('active', user.active);
      setActive(user.active);
    }
  }, [searchSuccess]);

  return (
    <div className={styles.formContainer}>
      <Form>
        <Flex container alignItems="center" className="mb-1">
          <div className="ml-auto">
            {!editable && (
              <Button color="primary" size="md" mode="solid" rounded="rounded-md" disabled={editable} onClick={() => setEditable(true)}>
                Abilita modifica
              </Button>
            )}
          </div>
        </Flex>
        <FormRow>
          <FormItem>
            <Label>Email</Label>
            {editable ? (
              <input
                type="text"
                autoComplete="off"
                defaultValue={user?.email}
                className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                {...register('email', validationSchema.email)}
              />
            ) : (
              <ReadOnly content={user?.email} />
            )}
            <FormError error={errors?.email} validation={validationSchema.email} />
          </FormItem>
        </FormRow>
        <FormRow>
          <FormItem>
            <Label>Mansione</Label>
            {editable ? (
              <Select options={authorityOptions} onSelect={selectAuthority} selected={user.authority.id} />
            ) : (
              <ReadOnly content={user?.authority.name} />
            )}
          </FormItem>
        </FormRow>
        {editable ? (
          <FormRow>
            <Label>{active ? 'Disattiva utente' : 'Attiva utente'}</Label>
            <Switcher switched={active} onSwitch={onActiveSwitch} />
          </FormRow>
        ) : (
          <>
            <FormRow>
              <Label>Stato</Label>
              <ReadOnly content={active ? 'Attivo' : 'Disattivo'} />
            </FormRow>
          </>
        )}
        {editable && (
          <FormRow col="2">
            <FormItem>
              <Button
                color="white"
                size="md"
                mode="outline"
                rounded="rounded-md"
                fullWidth
                disabled={!isValid}
                onClick={() => setEditable(false)}
              >
                Annulla
              </Button>
            </FormItem>
            <FormItem>
              <Button
                color="black"
                size="md"
                mode="solid"
                rounded="rounded-md"
                fullWidth
                disabled={!isValid}
                onClick={handleSubmit(onSave)}
              >
                Salva
              </Button>
            </FormItem>
          </FormRow>
        )}
      </Form>
    </div>
  );
};
